import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import type { HttpStateData } from '@/clients/http'
import type { ListQuery, ListResult } from '@/http/models/Resource'
import { apiErrorMessage } from '@/lib/apiError'

export const POS_PAGE_SIZE = 20

/** Loads one page of a POS list whenever the page or the filters change; filters reset the page. */
export const usePagedReport = <T,>(
  fetcher: (query: ListQuery) => Promise<HttpStateData<ListResult<T>, { message?: string }>>,
  filters: ListQuery,
) => {
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<T[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const filterKey = JSON.stringify(filters)

  useEffect(() => setPage(1), [filterKey])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetcher({ ...filters, page, length: POS_PAGE_SIZE }).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        setRows(res.data.result ?? [])
        setCount(res.data.count ?? 0)
      } else {
        toast.error(apiErrorMessage(res, 'Could not load the list'))
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterKey])

  return { rows, count, page, setPage, loading, pageSize: POS_PAGE_SIZE }
}
